import { ReactNode, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { LoaderFullscreen } from '@/components/layout/Loader';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import {
  FeatureEnum,
  PermissionEnum,
  StakeIroDetail_NegativeImpactIroFragment,
  StakeIroDetail_OpportunityIroFragment,
  StakeIroDetail_PositiveImpactIroFragment,
  StakeIroDetail_RiskIroFragment,
  SurveyLanguage,
  useStakeIroDetailQuery,
} from '@/graphql/generated';
import { IroRiskCard } from './IroCard/IroRiskCard';
import { IroPositiveImpactCard } from './IroCard/IroPositiveImpactCard';
import { IroNegativeImpactCard } from './IroCard/IroNegativeImpactCard';
import { IroOpportunityCard } from './IroCard/IroOpportunityCard';
import { tidyIros } from '../../../project/iro/tidyIros';
import { ProgressBarWithScale } from '@/components/generic/ProgressBarWithScale';
import LanguageDropdown from '@/components/form/LanguageDropdown';
import { ImpactMaterialityScore } from '../../../project/iro/evaluation/scores/ImpactMaterialityScore';
import { FinancialMaterialityScore } from '../../../project/iro/evaluation/scores/FinancialMaterialityScore';
import { StakeMaterialityReason } from './StakeMaterialityReason';
import { usePermissionChecker } from '@/hooks/usePermissionChecker';
import { getPersistedOrComputedMaterialityScore } from '@/services/ReferentialService';

export function StakeIroDetail({ stakeId }: { stakeId: string }) {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const { isGranted } = usePermissionChecker();
  const readOnly = !isGranted(FeatureEnum.IroCotation, PermissionEnum.Write);
  const [language, setLanguage] = useState<SurveyLanguage>(
    i18n.language as SurveyLanguage,
  );
  const { loading, data, error } = useStakeIroDetailQuery({
    variables: {
      stakeId: stakeId,
    },
    fetchPolicy: 'network-only',
  });

  const { risks, positiveImpacts, negativeImpacts, opportunities } = tidyIros<
    StakeIroDetail_RiskIroFragment,
    StakeIroDetail_OpportunityIroFragment,
    StakeIroDetail_PositiveImpactIroFragment,
    StakeIroDetail_NegativeImpactIroFragment
  >(data?.iros);

  if (loading) {
    return <LoaderFullscreen />;
  }

  if (error) {
    return <MessageBox type={MessageBoxType.Error}>{error.message}</MessageBox>;
  }
  const materialityScores = data?.stakeMateriality
    ? getPersistedOrComputedMaterialityScore(data.stakeMateriality)
    : { impactMaterialityScore: 0, financialMaterialityScore: 0 };

  return (
    <div className="p-4 space-y-8 divide-y divide-gray-100">
      <div className="space-y-2">
        <h5>{t('stake.detailModal.materiality.impactScore')}</h5>
        <div className="flex items-center gap-4">
          <div className="w-7/12">
            <ProgressBarWithScale
              percentageValue={materialityScores.impactMaterialityScore / 4}
              displayLevel={true}
            />
          </div>
          <div className="w-5/12 flex justify-end">
            {data?.stakeMateriality && (
              <ImpactMaterialityScore
                stake={data?.stakeMateriality}
                readOnly={readOnly}
              />
            )}
          </div>
        </div>
        <h5>{t('stake.detailModal.materiality.financialScore')}</h5>
        <div className="flex items-center gap-4">
          <div className="w-7/12">
            <ProgressBarWithScale
              percentageValue={materialityScores.financialMaterialityScore / 4}
              displayLevel={true}
            />
          </div>
          <div className="w-5/12 flex justify-end">
            {data?.stakeMateriality && (
              <FinancialMaterialityScore
                stake={data?.stakeMateriality}
                readOnly={readOnly}
              />
            )}
          </div>
        </div>
      </div>
      {data?.stakeMateriality && (
        <div className="pt-8">
          <StakeMaterialityReason
            stake={data?.stakeMateriality}
            readOnly={readOnly}
          />
        </div>
      )}
      <div>
        <div className="flex items-center justify-between pt-8">
          <h5>{t('screen.iro.impacts_risks_and_opportunities')}</h5>
          <LanguageDropdown
            currentLanguage={language}
            onUpdate={(language) => setLanguage(language)}
          />
        </div>
        <div className="space-y-4 divide-y divide-gray-100">
          <div className="space-y-2 pt-4">
            <div className="flex items-center justify-between gap-4">
              <IroTitle>{t('iro.types.NEGATIVE_IMPACT')}</IroTitle>
            </div>
            <div className="space-y-2">
              {negativeImpacts.length === 0 && <NoIro />}
              {negativeImpacts.map((iro) => (
                <IroNegativeImpactCard
                  key={`stakeIroDetailIroNegativeImpactCard-${iro.id}`}
                  {...{ iro, language }}
                />
              ))}
            </div>
          </div>
          <div className="space-y-2 pt-4">
            <div className="flex items-center justify-between gap-4">
              <IroTitle>{t('iro.types.POSITIVE_IMPACT')}</IroTitle>
            </div>
            <div className="space-y-2">
              {positiveImpacts.length === 0 && <NoIro />}
              {positiveImpacts.map((iro) => (
                <IroPositiveImpactCard
                  key={`stakeIroDetail_IroPositiveImpactCard-${iro.id}`}
                  {...{ iro, language }}
                />
              ))}
            </div>
          </div>
          <div className="space-y-2 pt-4">
            <div className="flex items-center justify-between gap-4">
              <IroTitle>{t('iro.types.RISK')}</IroTitle>
            </div>
            <div className="space-y-2">
              {risks.length === 0 && <NoIro />}
              {risks.map((iro) => (
                <IroRiskCard
                  key={`stakeIroDetail_IroRiskCard-${iro.id}`}
                  {...{ iro, language }}
                />
              ))}
            </div>
          </div>
          <div className="space-y-2 pt-4">
            <div className="flex items-center justify-between gap-4">
              <IroTitle>{t('iro.types.OPPORTUNITY')}</IroTitle>
            </div>
            <div className="space-y-2">
              {opportunities.length === 0 && <NoIro />}
              {opportunities.map((iro) => (
                <IroOpportunityCard
                  key={`stakeIroDetail_IroOpportunityCard-${iro.id}`}
                  {...{ iro, language }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function IroTitle({ children }: { children: ReactNode }) {
  return (
    <div className="font-bold first-letter:capitalize grow">{children}</div>
  );
}

function NoIro() {
  const { t } = useTranslation();
  return (
    <div className="text-gray-500 text-center italic sami-body-small">
      {t('global:none')}
    </div>
  );
}
